import { Avatar, type AvatarProps } from '../../components/Avatar';
import { Text } from '../../primitives/Text';
import { View } from '../../primitives/View';
import { cn } from '../../utils/cn';
import { counterTint } from '../../utils/color';
import type {
  MemberAvatarStackFace,
  MemberAvatarStackProps,
} from './MemberAvatarStack.types';

/**
 * Split a member list into the faces to draw and the ones a `+N` stands for.
 *
 * The rule that matters: the count only takes a slot when it BUYS something. At
 * exactly `maxFaces` a `+1` would replace the very member it represents, so the
 * split only happens once the list is genuinely longer.
 *
 * Exported for its own test — this is the part that was wrong on mobile (a hard
 * `slice(0, 2)` with no overflow at all, so a group of thirty read like a group
 * of two).
 */
export function splitMemberFaces(
  members: readonly MemberAvatarStackFace[],
  maxFaces: number,
): { faces: readonly MemberAvatarStackFace[]; hidden: readonly MemberAvatarStackFace[] } {
  if (members.length <= maxFaces) return { faces: members, hidden: [] };
  return { faces: members.slice(0, maxFaces - 1), hidden: members.slice(maxFaces - 1) };
}

/**
 * Per-size negative margin for `overlap="tight"`.
 *
 * The default overlap is a flat -10px at every size, which reads as a row of
 * near-separate circles once the faces are big: at `l` (40px) each face still
 * shows 75% of itself. The follow-nudge stack (Figma `Social features`
 * 18:21856 — three 40px faces at x = 0 / 10 / 20, a 60px stack) wants the
 * opposite reading, one face with the rest tucked behind it, so it asks for
 * `tight`.
 *
 * Each value is -(size + 2 - 10), for a 10px sliver: the +2 is the slot's ring,
 * which the margin has to swallow too, or every sliver comes out 2px wide of
 * the design.
 *
 * An explicit map because the classes have to be statically visible to
 * Tailwind / NativeWind — a computed arbitrary value compiles to nothing.
 */
const TIGHT_OVERLAP: Record<NonNullable<AvatarProps['size']>, string> = {
  '2xs': '-ml-[12px]',
  xs: '-ml-[20px]',
  s: '-ml-[24px]',
  m: '-ml-[28px]',
  l: '-ml-[32px]',
  xl: '-ml-[36px]',
  '2xl': '-ml-[40px]',
  '3xl': '-ml-[44px]',
  '4xl': '-ml-[117px]',
};

/**
 * `+N` footprint per DS Avatar size — the faces' own h/w steps, but `min-w`
 * instead of `w` so a two-digit count grows into a pill instead of clipping.
 * Explicit on purpose: the bubble used to size itself `h-full` inside an
 * auto-height slot, which resolves to the TEXT's height on both platforms —
 * the "+9" next to 32px faces was a 14px dot.
 */
const COUNTER_SIZE: Record<NonNullable<AvatarProps['size']>, string> = {
  '2xs': 'h-5 min-w-5',
  xs: 'h-7 min-w-7',
  s: 'h-8 min-w-8',
  m: 'h-9 min-w-9',
  l: 'h-10 min-w-10',
  xl: 'h-11 min-w-11',
  '2xl': 'h-12 min-w-12',
  '3xl': 'h-[52px] min-w-[52px]',
  '4xl': 'h-[125px] min-w-[125px]',
};

/**
 * An overlapped row of member avatars ending in a `+N` once the group outgrows
 * the space — the group header's member stack, shared by web and mobile.
 *
 * Web and mobile had this twice: web showed 4 faces then a tooltip-backed `+N`,
 * the phone hard-capped at 2 faces and dropped the rest silently. The count and
 * the size differ per surface (a phone top bar shares its row with three
 * chips), so those are props; the anatomy — overlap, ring, the `+N` bubble, and
 * above all WHEN the count replaces a face — is fixed here.
 *
 * Interaction is injected: web wraps each face in a profile link + tooltip via
 * `renderFace`, while the phone makes the whole stack one press target in its
 * caller. Neither belongs in the DS.
 */
export function MemberAvatarStack({
  members,
  maxFaces = 4,
  size = 's',
  overlap = 'default',
  color,
  renderFace,
  renderOverflow,
  className,
  testID,
}: MemberAvatarStackProps) {
  const { faces, hidden } = splitMemberFaces(members, maxFaces);
  if (faces.length === 0) return null;
  // StudyGroupCard's counter: the group colour's tint pair, inline because it
  // is an arbitrary runtime hex (mode-invariant, like the card).
  const tint = color ? counterTint(color) : null;
  const bubbleStyle = tint && { style: { backgroundColor: tint.backgroundColor } };
  const labelStyle = tint && { style: { color: tint.color } };

  // `-ml` on every slot after the first: the DS ring is drawn by the wrapper so
  // the overlap reads as a stack rather than touching circles.
  const overlapClass = overlap === 'tight' ? TIGHT_OVERLAP[size] : '-ml-2.5';
  const slot = (key: string, index: number, child: React.ReactElement) => (
    <View
      key={key}
      className={cn(
        'rounded-full border border-secondary-50',
        index > 0 && overlapClass,
      )}>
      {child}
    </View>
  );

  return (
    <View className={cn('flex-row items-center', className)} testID={testID}>
      {faces.map((face, index) => {
        const avatar = (
          <Avatar size={size} src={face.avatarUrl || undefined} name={face.name ?? ''} />
        );
        return slot(
          face.id,
          index,
          (renderFace ? renderFace(face, avatar) : avatar) as React.ReactElement,
        );
      })}
      {hidden.length > 0
        ? slot(
            'overflow',
            faces.length,
            (() => {
              const bubble = (
                <View
                  className={cn(
                    'flex-row items-center justify-center rounded-full px-1.5',
                    COUNTER_SIZE[size],
                    !tint && 'bg-secondary-200',
                  )}
                  webProps={bubbleStyle || undefined}
                  nativeProps={bubbleStyle || undefined}>
                  <Text
                    className={cn(
                      'text-xs font-bold !leading-[normal] native:font-mona-bold',
                      !tint && 'text-subtitle-800',
                    )}
                    webProps={labelStyle || undefined}
                    nativeProps={labelStyle || undefined}
                    selectable={false}>
                    {`+${hidden.length}`}
                  </Text>
                </View>
              );
              return (
                renderOverflow ? renderOverflow(hidden, bubble) : bubble
              ) as React.ReactElement;
            })(),
          )
        : null}
    </View>
  );
}
(MemberAvatarStack as { displayName?: string }).displayName = 'MemberAvatarStack';
